import { useState, useEffect } from 'react';
import { DialogTitle } from '@headlessui/react';
import { cn } from 'src/utils/cn';
import BaseModal from 'src/components/BaseModal';

export interface VersionLabel {
  internal: string;
  display: string;
}

interface VersionSelectionModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (internalVersion: string | null) => void;
  readonly labels: VersionLabel[];
  /** What choosing a version does. Defaults to the rule builder's wording;
   *  the income formula editor says "formula" instead. */
  readonly description?: string;
}

export function VersionSelectionModal({
  open,
  onClose,
  onConfirm,
  labels,
  description = 'Selecting a version restricts rule evaluation to that specific version. You can skip if version is not required.',
}: VersionSelectionModalProps) {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedVersion(null);
    }
  }, [open]);

  const handleConfirm = (): void => {
    onConfirm(selectedVersion);
    onClose();
  };

  const handleSkip = (): void => {
    onConfirm(null);
    onClose();
  };

  return (
    <BaseModal open={open} onClose={onClose} size="lg">
      <div>
        <DialogTitle
          as="h3"
          className="text-base font-semibold text-gray-900 mb-4"
        >
          Select what version we need to evaluate for this document type
        </DialogTitle>

        <div className="space-y-4">
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {labels.map((label) => (
              <label
                key={label.internal}
                className="flex items-center space-x-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="versionSelection"
                  value={label.internal}
                  checked={selectedVersion === label.internal}
                  onChange={() => setSelectedVersion(label.internal)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm font-medium text-gray-900">
                  {label.display}
                </span>
              </label>
            ))}
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-xs text-gray-600">{description}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-row-reverse gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedVersion}
            className={cn(
              'inline-flex justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm',
              selectedVersion
                ? 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            )}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
          >
            Skip Configuring Version
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 mr-auto"
          >
            Cancel
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
